import React, { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, fmtNum } from "../components/Shared";
import { toast } from "sonner";
import {
  Search, Loader2, ClipboardCheck, ChevronLeft, ChevronRight, Check, Info, Lock, LockOpen,
} from "lucide-react";

const PAGE = 50;

const FILTERS = [
  { key: "", label: "Semua" },
  { key: "uncounted", label: "Belum Dihitung" },
  { key: "counted", label: "Sudah Dihitung" },
];

function expiryInfo(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const days = Math.ceil((d - new Date()) / 86400000);
  if (days < 0) return { label: "Expired", cls: "bg-red-50 text-red-600 border-red-200" };
  if (days <= 90) return { label: `${days}h lagi`, cls: "bg-amber-50 text-amber-600 border-amber-200" };
  return null;
}

export default function StockOpname() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [locs, setLocs] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [lock, setLock] = useState(null);
  const [lockBusy, setLockBusy] = useState(false);
  const locked = !!lock?.locked;

  const loadLock = useCallback(() => {
    api.get("/so-lock").then((r) => setLock(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadLock(); }, [loadLock]);

  const toggleLock = async () => {
    setLockBusy(true);
    try {
      if (locked) {
        await api.post("/so-lock/unlock");
        toast.success("Periode SO dibuka");
      } else {
        const period = new Date().toLocaleDateString("id-ID", { year: "numeric", month: "long" });
        await api.post("/so-lock/lock", { period });
        toast.success("Periode SO dikunci");
      }
      loadLock();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLockBusy(false); }
  };

  useEffect(() => {
    api.get("/locations").then((r) => {
      setLocs(r.data);
      if (r.data.length) setLocationId((prev) => prev || r.data[0].id);
    }).catch(() => {});
  }, []);

  const load = useCallback(async (p, s, loc, st) => {
    if (!loc) { setItems([]); setTotal(0); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/opname/items", {
        params: { location_id: loc, search: s, status: st, skip: p * PAGE, limit: PAGE },
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setPage(0); load(0, search, locationId, status); }, 300);
    return () => clearTimeout(t);
  }, [search, locationId, status, load]);

  const setDraft = (id, field, val) => setDrafts((d) => ({ ...d, [id]: { ...d[id], [field]: val } }));

  const saveRow = async (m) => {
    const draft = drafts[m.id_barang] || {};
    const fisikRaw = draft.fisik !== undefined ? draft.fisik : (m.quantity_fisik ?? "");
    const fisik = fisikRaw === "" ? null : Number(fisikRaw);
    const ket = draft.ket !== undefined ? draft.ket : (m.keterangan || "");
    const exp = draft.exp !== undefined ? draft.exp : (m.tanggal_expired || "");
    const kartuRaw = draft.kartu !== undefined ? draft.kartu : (m.qty_kartu_stock ?? "");
    const kartu = kartuRaw === "" ? null : Number(kartuRaw);
    setSavingId(m.id_barang);
    try {
      const { data } = await api.put("/opname/count", {
        location_id: locationId, id_barang: m.id_barang,
        quantity_fisik: fisik, keterangan: ket, tanggal_expired: exp, qty_kartu_stock: kartu,
      });
      setItems((its) => its.map((it) => (it.id_barang === m.id_barang ? { ...it, ...data } : it)));
      setDrafts((d) => { const n = { ...d }; delete n[m.id_barang]; return n; });
      toast.success("Perhitungan disimpan");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSavingId(null); }
  };

  const totalPages = Math.ceil(total / PAGE);
  const currentLoc = locs.find((l) => l.id === locationId);

  return (
    <div className="pb-12">
      <PageHeader title="Stock Opname" subtitle="Hitung fisik obat per tempat penyimpanan (sesuai gudang)" />

      <div className="px-4 sm:px-6 lg:px-8 pt-6">
        <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border px-4 py-3 ${locked ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`} data-testid="so-lock-bar">
          <div className="flex items-center gap-2 text-sm">
            {locked ? <Lock className="h-4 w-4 text-amber-600" /> : <LockOpen className="h-4 w-4 text-emerald-600" />}
            <span className={locked ? "text-amber-800 font-semibold" : "text-slate-600"}>
              {locked ? `Periode terkunci${lock?.period ? ` · ${lock.period}` : ""} — perhitungan dinonaktifkan` : "Periode terbuka — perhitungan aktif"}
            </span>
          </div>
          {isAdmin && (
            <button
              onClick={toggleLock}
              disabled={lockBusy}
              data-testid="so-lock-toggle-btn"
              className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-semibold transition-colors disabled:opacity-50 ${
                locked ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-amber-600 text-white hover:bg-amber-700"
              }`}
            >
              {lockBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : locked ? <LockOpen className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
              {locked ? "Buka Kunci Periode" : "Kunci Periode SO"}
            </button>
          )}
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 pt-6 space-y-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              data-testid="opname-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari obat..."
              className="w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600"
            />
          </div>
          <select
            data-testid="opname-location-select"
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600 min-w-[240px]"
          >
            <option value="">— pilih tempat penyimpanan —</option>
            {locs.map((l) => (
              <option key={l.id} value={l.id}>{l.nama}{l.gudang ? ` · ${l.gudang}` : ""} ({fmtNum(l.total_obat)})</option>
            ))}
          </select>
        </div>

        <div className="flex items-start gap-2 rounded-lg bg-sky-50 border border-sky-100 px-3 py-2 text-xs text-sky-700">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Menampilkan semua obat master pada gudang <b>{currentLoc?.gudang || "—"}</b>. Qty Awal adalah <b>saldo master</b> (total). Selisih saldo akhir (gabungan semua tempat) dihitung otomatis di menu <b>Saldo Master</b>.
          </span>
        </div>

        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key || "all"}-btn`}
              onClick={() => setStatus(f.key)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                status === f.key ? "bg-teal-700 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left">
                  {["Nama Obat", "ID Barang", "Satuan", "Qty Awal (Saldo)", "Qty Fisik", "Qty Kartu Stock", "Kadaluarsa", "Keterangan", ""].map((h, i) => (
                    <th key={i} className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
                ) : !locationId ? (
                  <tr><td colSpan={9} className="px-4 py-16 text-center">
                    <ClipboardCheck className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm">Pilih tempat penyimpanan untuk memulai perhitungan.</p>
                  </td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-16 text-center">
                    <ClipboardCheck className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm">Tidak ada obat pada gudang ini. Impor master obat atau ubah filter.</p>
                  </td></tr>
                ) : items.map((m) => {
                  const draft = drafts[m.id_barang] || {};
                  const fisikVal = draft.fisik !== undefined ? draft.fisik : (m.quantity_fisik ?? "");
                  const ketVal = draft.ket !== undefined ? draft.ket : (m.keterangan || "");
                  const expVal = draft.exp !== undefined ? draft.exp : (m.tanggal_expired || "");
                  const kartuVal = draft.kartu !== undefined ? draft.kartu : (m.qty_kartu_stock ?? "");
                  const dirty = draft.fisik !== undefined || draft.ket !== undefined || draft.exp !== undefined || draft.kartu !== undefined;
                  const exp = expiryInfo(expVal);
                  return (
                    <tr key={m.id_barang} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors" data-testid="opname-row">
                      <td className="px-4 py-2.5 font-medium text-slate-800 max-w-xs">{m.nama}</td>
                      <td className="px-4 py-2.5 font-mono-t text-xs text-slate-500">{m.id_barang || "-"}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-500 uppercase">{m.satuan_jual || "-"}</td>
                      <td className="px-4 py-2.5 text-right tabular font-mono-t text-slate-700">{fmtNum(m.quantity_awal)}</td>
                      <td className="px-4 py-2.5">
                        <input
                          type="number"
                          data-testid={`physical-qty-input-${m.id_barang}`}
                          value={fisikVal}
                          disabled={locked}
                          onChange={(e) => setDraft(m.id_barang, "fisik", e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") saveRow(m); }}
                          className="w-24 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm text-right tabular font-mono-t focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600 disabled:bg-slate-100 disabled:cursor-not-allowed"
                          placeholder="—"
                        />
                      </td>
                      <td className="px-4 py-2.5">
                        <input
                          type="number"
                          data-testid={`kartu-stock-input-${m.id_barang}`}
                          value={kartuVal}
                          disabled={locked}
                          onChange={(e) => setDraft(m.id_barang, "kartu", e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") saveRow(m); }}
                          className="w-28 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm text-right tabular font-mono-t focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600 disabled:bg-slate-100 disabled:cursor-not-allowed"
                          placeholder="—"
                        />
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-col gap-1">
                          <input
                            type="date"
                            data-testid={`expired-input-${m.id_barang}`}
                            value={expVal}
                            disabled={locked}
                            onChange={(e) => setDraft(m.id_barang, "exp", e.target.value)}
                            className="w-36 rounded-md border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600 disabled:bg-slate-100 disabled:cursor-not-allowed"
                          />
                          {exp && <span className={`inline-flex w-fit items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold ${exp.cls}`}>{exp.label}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <input
                          value={ketVal}
                          disabled={locked}
                          onChange={(e) => setDraft(m.id_barang, "ket", e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") saveRow(m); }}
                          className="w-40 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600 disabled:bg-slate-100 disabled:cursor-not-allowed"
                          placeholder="catatan..."
                        />
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <button
                          onClick={() => saveRow(m)}
                          disabled={savingId === m.id_barang || locked}
                          data-testid={`save-opname-row-${m.id_barang}`}
                          className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            dirty ? "bg-teal-700 text-white hover:bg-teal-800" : m.counted ? "text-emerald-600 bg-emerald-50" : "text-slate-400 bg-slate-50 hover:bg-slate-100"
                          }`}
                        >
                          {savingId === m.id_barang ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                          {dirty ? "Simpan" : m.counted ? "OK" : "Simpan"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <span className="text-xs text-slate-500">Halaman {page + 1} dari {totalPages} · {fmtNum(total)} obat</span>
              <div className="flex gap-2">
                <button disabled={page === 0} onClick={() => { const p = page - 1; setPage(p); load(p, search, locationId, status); }} className="p-1.5 rounded-md border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
                <button disabled={page + 1 >= totalPages} onClick={() => { const p = page + 1; setPage(p); load(p, search, locationId, status); }} className="p-1.5 rounded-md border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
